import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './QuillEditor.css'; // Import stiluri custom pentru dark theme
import apiClient from '../services/api';
import { getAccessToken } from '../utils/tokenStorage';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Save as SaveIcon,
  CheckCircle as FinalizeIcon,
  History as HistoryIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Article as TemplateIcon,
  SmartToy as AiIcon
} from '@mui/icons-material';
import TemplateSelector from './reports/TemplateSelector';
import SaveAsTemplateDialog from './reports/SaveAsTemplateDialog';
import AiRefactorComparisonDialog from './reports/AiRefactorComparisonDialog';

// Configurare Quill Editor
const QUILL_MODULES = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'script': 'sub'}, { 'script': 'super' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    ['link', 'image'],
    ['clean']
  ],
  history: {
    delay: 1000,
    maxStack: 100,
    userOnly: true
  }
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'script', 'indent',
  'color', 'background', 'align',
  'link', 'image'
];

interface Segment {
  id: string;
  body_part_code: string;
  display_name?: string;
  modality: string;
  contrast_status: string;
  status: string;
  assigned_to_doctor_id: string | null;
  assigned_doctor_name?: string | null;
}

interface ReportEditorProps {
  reportId?: string;
  studyId: string;
  segmentId?: string; // ✨ NEW - pentru rapoarte per segment
  initialContent?: string;
  initialJson?: any;
  status?: 'draft' | 'final';
  onSave?: (data: { html: string; editor_html: string; editor_json: any }) => void;
  onFinalize?: () => void;
  onView?: () => void;
  onHistory?: () => void;
  onDownload?: () => void;
  onEdit?: () => void;
  readOnly?: boolean;
  patientInfo?: {
    name: string;
    cnp: string;
    dob: string;
    sex: string;
  };
  studyInfo?: {
    description: string;
    date: string;
    modality: string;
  };
}

const ReportEditor: React.FC<ReportEditorProps> = ({
  reportId: initialReportId,
  studyId,
  segmentId: initialSegmentId,
  initialContent = '',
  initialJson = null,
  status = 'draft',
  onSave,
  onFinalize: _onFinalize,
  onView,
  onHistory,
  onDownload,
  onEdit,
  readOnly = false,
  patientInfo,
  studyInfo
}) => {
  const { user } = useAuth();
  const [reportId, setReportId] = useState(initialReportId);
  const [content, setContent] = useState(initialContent);
  const [_editorJson, setEditorJson] = useState(initialJson);
  const [currentStatus, setCurrentStatus] = useState(status);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const quillRef = useRef<ReactQuill>(null);
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  
  // ✅ FIX: Update content when initialContent changes
  useEffect(() => {
    if (initialContent && initialContent !== content) {
      console.log('ReportEditor: Updating content from initialContent, length:', initialContent.length);
      setContent(initialContent);
      
      // Update Quill editor if available
      const quill = quillRef.current?.getEditor();
      if (quill && initialContent) {
        quill.root.innerHTML = initialContent;
      }
    }
  }, [initialContent]);
  
  // ✅ FIX: Update reportId when it changes
  useEffect(() => {
    if (initialReportId && initialReportId !== reportId) {
      console.log('ReportEditor: Updating reportId:', initialReportId);
      setReportId(initialReportId);
    }
  }, [initialReportId]);
  
  // ✅ FIX: Update status when it changes
  useEffect(() => {
    if (status && status !== currentStatus) {
      console.log('ReportEditor: Updating status:', status);
      setCurrentStatus(status);
    }
  }, [status]);
  
  // ✨ NEW - Segment support
  const [segments, setSegments] = useState<Segment[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | undefined>(initialSegmentId);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  
  // ✨ AI Refactoring state
  const [aiRefactorDialogOpen, setAiRefactorDialogOpen] = useState(false);
  
  // ✨ Save as Template state
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  
  // Get current segment for display
  const currentSegment = segments.find(s => s.id === selectedSegmentId);
  const formatSegmentName = (segment: Segment): string => {
    return segment.display_name || segment.body_part_code;
  };
  
  // ✨ SIMPLIFIED - Load DOAR segmentele medicului
  useEffect(() => {
    const loadSegments = async () => {
      if (!user) return;
      
      setLoadingSegments(true);
      try {
        // Load only doctor's segments (backend already filters for medics)
        const response = await apiClient.get(`/segments/?study_id=${studyId}`);
        const doctorSegments = response.data || [];
        setSegments(doctorSegments);
        
        // Auto-select first segment
        if (doctorSegments.length === 1) {
          setSelectedSegmentId(doctorSegments[0].id);
        } else if (doctorSegments.length > 0) {
          setSelectedSegmentId(doctorSegments[0].id);
        }
      } catch (err) {
        console.error('Error loading segments:', err);
      } finally {
        setLoadingSegments(false);
      }
    };
    
    loadSegments();
  }, [studyId, user]);

  // ✨ Încarcă raportul existent pentru STUDY (cu multi-segment structure)
  useEffect(() => {
    const loadExistingReport = async () => {
      if (reportId || readOnly) return;
      
      // ✨ CRITICAL: Wait for segments to load first before filtering!
      if (!segments || segments.length === 0) return;
      
      setLoading(true);
      try {
        // Caută raport per STUDY (poate avea multi-segment structure)
        const response = await fetch(`/api/v1/reports/?studyId=${studyId}&page=1&size=1`, {
          headers: {
            'Authorization': `Bearer ${getAccessToken()}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.reports && data.reports.length > 0) {
            const existingReport = data.reports[0];
            setReportId(existingReport.id);
            
            // Filter editor_json to only show assigned segments
            const filteredJson: {segments: Record<string, any>} = {segments: {}};
            if (existingReport.editor_json?.segments) {
              segments.forEach(seg => {
                if (existingReport.editor_json.segments[seg.body_part_code]) {
                  filteredJson.segments[seg.body_part_code] = existingReport.editor_json.segments[seg.body_part_code];
                }
              });
            }
            
            setEditorJson(filteredJson);
            setCurrentStatus(existingReport.status || 'draft');
            // Content will be loaded when segment is selected
          }
        }
      } catch (err) {
        console.error('Error loading existing report:', err);
      } finally {
        setLoading(false);
      }
    };
    loadExistingReport();
  }, [studyId, reportId, readOnly, segments]);

  // Update content when selected segment changes (restore from multi-segment structure)
  useEffect(() => {
    if (!selectedSegmentId || !currentSegment) return;
    
    // ✅ FIX: Check if this is a legacy report (no segment structure)
    const hasSegmentStructure = _editorJson && _editorJson.segments && Object.keys(_editorJson.segments).length > 0;
    
    // Extract content for selected segment from multi-segment structure
    const segmentData = _editorJson?.segments?.[currentSegment.body_part_code];
    
    // ✅ FIX: For legacy reports without segment structure, keep the original content
    // Don't clear it when switching segments
    if (!hasSegmentStructure && initialContent) {
      console.log('ReportEditor: Legacy report detected, preserving original content');
      // Don't update content - keep the initialContent that was loaded
      return;
    }
    
    const newContent = segmentData?.content_html || '';
    
    setContent(newContent);
    
    // Update Quill editor content
    const quill = quillRef.current?.getEditor();
    if (quill && segmentData?.content_json) {
      quill.setContents(segmentData.content_json);
    } else if (quill) {
      // ✅ FIX: Only clear if we actually have segment structure
      // Don't clear for legacy reports
      if (hasSegmentStructure) {
        quill.setText('');
      }
    }
  }, [selectedSegmentId, currentSegment, _editorJson, initialContent]);

  // Auto-save la fiecare 30 secunde
  const autoSave = useCallback(async () => {
    if (!reportId || readOnly || currentStatus === 'final') return;

    setIsSaving(true);
    setSaveStatus('saving');

    try {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      const html = quill.root.innerHTML;
      const json = quill.getContents();

      // Salvare către backend
      const response = await fetch(`/api/v1/reports/${reportId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`
        },
        body: JSON.stringify({
          editor_html: html,
          editor_json: json
        })
      });

      if (response.ok) {
        setSaveStatus('saved');
        setError(null);
        onSave?.({ html, editor_html: html, editor_json: json });
      } else {
        throw new Error('Eroare la salvare');
      }
    } catch (err) {
      setSaveStatus('error');
      setError('Eroare la salvare automată');
      console.error('Auto-save error:', err);
    } finally {
      setIsSaving(false);
    }
  }, [reportId, readOnly, status, onSave]);

  // Setup auto-save
  useEffect(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    if (!readOnly && currentStatus === 'draft') {
      autoSaveTimeoutRef.current = setTimeout(autoSave, 30000); // 30 secunde
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [content, autoSave, readOnly, currentStatus]);

  // Manual save
  const handleManualSave = async () => {
    if (readOnly || currentStatus === 'final') return;
    if (!selectedSegmentId || !currentSegment) {
      setError('Te rog selectează un segment pentru a salva raportul');
      return;
    }

    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const html = quill.root.innerHTML;
    const json = quill.getContents();

    setIsSaving(true);
    setSaveStatus('saving');

    try {
      // ✨ Save with multi-segment structure
      const updatedJson = _editorJson || {segments: {}};
      if (!updatedJson.segments) {
        updatedJson.segments = {};
      }
      
      updatedJson.segments[currentSegment.body_part_code] = {
        content_html: html,
        content_json: json
      };

      let response;
      if (reportId) {
        // Update with multi-segment structure
        response = await apiClient.put(`/reports/${reportId}`, { editor_html: html, editor_json: updatedJson });
      } else {
        // Create with multi-segment structure (first segment creates the report)
        response = await apiClient.post(`/reports`, { study_id: studyId, segment_id: selectedSegmentId, editor_html: html, editor_json: updatedJson, draft: true });
      }

      if (response.status === 200 || response.status === 201) {
        const savedReport = response.data;
        if (!reportId && savedReport.id) {
          setReportId(savedReport.id);
        }
        setSaveStatus('saved');
        setError(null);
        onSave?.({ html, editor_html: html, editor_json: json });
      } else {
        throw new Error('Eroare la salvare');
      }
    } catch (err) {
      setSaveStatus('error');
      setError('Eroare la salvare');
      console.error('Save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Finalizare raport per segment (doar segmentele medicului)
  const handleFinalize = async () => {
    if (readOnly || currentStatus === 'final') {
      toast.success('Raportul este deja finalizat');
      return;
    }
    if (!currentSegment) return;

    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const html = quill.root.innerHTML;
    const json = quill.getContents();

    setIsSaving(true);

    try {
      // Save with multi-segment structure before finalizing
      const updatedJson = _editorJson || {segments: {}};
      if (!updatedJson.segments) {
        updatedJson.segments = {};
      }
      
      updatedJson.segments[currentSegment.body_part_code] = {
        content_html: html,
        content_json: json
      };

      let finalReportId = reportId;

      if (reportId) {
        await apiClient.put(`/reports/${reportId}`, { editor_html: html, editor_json: updatedJson });
      } else {
        const createResp = await apiClient.post(`/reports`, {
          study_id: studyId,
          segment_id: selectedSegmentId,
          editor_html: html,
          editor_json: updatedJson,
          draft: true
        });
        if (createResp.status === 200 || createResp.status === 201) {
          const savedReport = createResp.data;
          if (savedReport?.id) {
            finalReportId = savedReport.id;
            setReportId(savedReport.id);
          }
        }
      }

      // Finalize
      if (finalReportId) {
        const finalizeResp = await apiClient.post(`/reports/${finalReportId}/finalize`, { sign: true });
        if (finalizeResp.status === 200) {
          setCurrentStatus('final');
          setSaveStatus('saved');
          toast.success('Raport finalizat cu succes');
          
          // ✅ Apelează callback-ul pentru a închide dialogul și actualiza lista
          if (_onFinalize) {
            _onFinalize();
          }
        }
      } else {
        toast.error('Eroare: Nu s-a putut crea raportul');
      }
    } catch (err) {
      console.error('Finalize error:', err);
      const errorMessage = (err as any)?.response?.data?.detail || 'Eroare la finalizarea raportului';
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleContentChange = (content: string, _delta: any, _source: any, editor: any) => {
    if (!currentSegment) return;
    
    setContent(content);
    
    // ✨ NEW - Update multi-segment structure
    const updatedJson = _editorJson || {segments: {}};
    if (!updatedJson.segments) {
      updatedJson.segments = {};
    }
    
    updatedJson.segments[currentSegment.body_part_code] = {
      content_html: content,
      content_json: editor.getContents()
    };
    
    setEditorJson(updatedJson);
    
    // Reset save status când utilizatorul începe să editeze
    if (saveStatus === 'saved') {
      setSaveStatus('idle');
    }
  };

  // ✨ NEW - Handle segment selection change (switch between segments in same report)
  const handleSegmentChange = (newSegmentId: string) => {
    setSelectedSegmentId(newSegmentId);
    // Nu mai resetăm reportId - e același raport, doar schimbăm secțiunea vizibilă
  };
  
  // ✨ NEW - Handle template application
  const handleApplyTemplate = async (templateHtml: string, templateJson: any) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    // Set content from template
    setContent(templateHtml);
    setEditorJson(templateJson);
    quill.setContents(templateJson);
    
    setTemplateSelectorOpen(false);
    
    // 🔧 FIX: Save immediately after applying template
    if (!selectedSegmentId || !currentSegment) {
      toast.warning('Selectează un segment înainte de a aplica template-ul');
      return;
    }
    
    setIsSaving(true);
    setSaveStatus('saving');
    
    try {
      const updatedJson = { segments: {} };
      updatedJson.segments[currentSegment.body_part_code] = {
        content_html: templateHtml,
        content_json: templateJson
      };
      
      let response;
      if (reportId) {
        // Update existing report
        response = await apiClient.put(`/reports/${reportId}`, { 
          editor_html: templateHtml, 
          editor_json: updatedJson 
        });
      } else {
        // Create new report
        response = await apiClient.post(`/reports`, { 
          study_id: studyId, 
          segment_id: selectedSegmentId, 
          editor_html: templateHtml, 
          editor_json: updatedJson, 
          draft: true 
        });
        
        if (response.status === 200 || response.status === 201) {
          const savedReport = response.data;
          if (savedReport?.id) {
            setReportId(savedReport.id);
          }
        }
      }
      
      setSaveStatus('saved');
      toast.success('Template aplicat și salvat');
      onSave?.({ html: templateHtml, editor_html: templateHtml, editor_json: updatedJson });
    } catch (err) {
      console.error('Error saving template:', err);
      toast.error('Template aplicat dar nu s-a putut salva');
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header cu informații pacient și studiu - Compact */}
      {patientInfo && studyInfo && (
        <Paper sx={{ px: 1.5, py: 0.75, mb: 1, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>{patientInfo.name}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                CNP: {patientInfo.cnp} | Data nașterii: {(() => {
                  try {
                    if (!patientInfo.dob || patientInfo.dob === 'N/A') return 'N/A';
                    const d = new Date(patientInfo.dob);
                    if (isNaN(d.getTime())) return patientInfo.dob;
                    return d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
                  } catch { return patientInfo.dob; }
                })()} | Sex: {patientInfo.sex}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{studyInfo.date ? new Date(studyInfo.date).toLocaleDateString('ro-RO') : 'N/A'} | {studyInfo.modality}</Typography>
                <Chip label={currentStatus === 'draft' ? 'Draft' : 'Finalizat'} color={currentStatus === 'draft' ? 'default' : 'success'} size="small" sx={{ height: 18, fontSize: '0.62rem' }} />
              </Box>
            </Box>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', textAlign: 'right' }}>{studyInfo.description}</Typography>
          </Box>
        </Paper>
      )}
      
      {/* Alert dacă nu există segment selectat */}
      {!selectedSegmentId && segments.length > 0 && (
        <Alert severity="info" sx={{ mb: 1, py: 0.25, '& .MuiAlert-message': { fontSize: '0.78rem' } }}>Te rog selectează un segment pentru a începe scrierea raportului.</Alert>
      )}

      {/* Toolbar cu acțiuni - Compact */}
      <Paper sx={{ px: 1, py: 0.5, mb: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between">
          
          {/* LEFT: Segment Selector */}
          {segments.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180, '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}>
              <InputLabel id="segment-select-label">Segment</InputLabel>
              <Select labelId="segment-select-label" value={selectedSegmentId || ''} label="Segment" onChange={(e) => handleSegmentChange(e.target.value)} disabled={readOnly || currentStatus === 'final' || segments.length === 1}>
                {segments.map((segment) => (<MenuItem key={segment.id} value={segment.id} sx={{ fontSize: '0.8rem' }}>{formatSegmentName(segment)}</MenuItem>))}
              </Select>
            </FormControl>
          )}
          
          {/* RIGHT: Action Buttons - Compact */}
          <Stack direction="row" spacing={0.5}>
            <Button variant="contained" startIcon={<SaveIcon sx={{ fontSize: 16 }} />} onClick={handleManualSave} disabled={isSaving || readOnly || currentStatus === 'final'} size="small" sx={{ height: 30, fontSize: '0.75rem', textTransform: 'none', px: 1.5 }}>Salvează</Button>

          {currentStatus === 'draft' && !readOnly && (
            <Button variant="contained" color="success" startIcon={<FinalizeIcon sx={{ fontSize: 16 }} />} onClick={handleFinalize} disabled={isSaving} size="small" sx={{ height: 30, fontSize: '0.75rem', textTransform: 'none', px: 1.5 }}>Finalizează</Button>
          )}
          
          {/* Template button */}
          {currentStatus === 'draft' && !readOnly && (
            <Button variant="outlined" startIcon={<TemplateIcon sx={{ fontSize: 16 }} />} onClick={() => setTemplateSelectorOpen(true)} disabled={isSaving} size="small" sx={{ height: 30, fontSize: '0.75rem', textTransform: 'none', px: 1.5 }}>Template-uri</Button>
          )}
          
          {/* Save as Template button */}
          {currentStatus === 'draft' && !readOnly && content && content.trim().length > 20 && (
            <Tooltip title="Salvează conținutul curent ca template">
              <IconButton onClick={() => setSaveAsTemplateOpen(true)} disabled={isSaving} size="small" sx={{ p: 0.5 }}>
                <SaveIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          
          {/* ✨ AI Refactoring button - COMMENTED OUT (not used currently)
          {currentStatus === 'draft' && !readOnly && reportId && content && content.trim().length > 20 && (
            <Tooltip title="Refactorizează raportul folosind AI (funcție experimentală)">
              <Button
                variant="outlined"
                color="warning"
                startIcon={<AiIcon />}
                onClick={() => setAiRefactorDialogOpen(true)}
                disabled={isSaving}
                size="small"
                sx={{ 
                  position: 'relative',
                  '&::after': {
                    content: '"BETA"',
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    fontSize: '0.6rem',
                    fontWeight: 'bold',
                    backgroundColor: 'warning.main',
                    color: 'warning.contrastText',
                    padding: '1px 4px',
                    borderRadius: '4px',
                  }
                }}
              >
                AI Refactor
              </Button>
            </Tooltip>
          )}
          */}
          
          {readOnly && currentStatus === 'draft' && onEdit && (
            <Tooltip title="Editare">
              <IconButton onClick={onEdit} size="small" color="primary">
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Spacer pentru a împinge butoanele de navigare către dreapta */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Butoane de navigare/vizualizare - grupate la dreapta */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {onView && (
              <Tooltip title="Vizualizare">
                <IconButton onClick={onView} size="small">
                  <ViewIcon />
                </IconButton>
              </Tooltip>
            )}

            {onHistory && (
              <Tooltip title="Istoric versiuni">
                <IconButton onClick={onHistory} size="small">
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
            )}

            {onDownload && currentStatus === 'final' && (
              <Tooltip title="Download PDF">
                <IconButton onClick={onDownload} size="small">
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Separator vizual */}
          <Box sx={{ width: '1px', height: '24px', bgcolor: 'divider', mx: 1 }} />

          {/* Status de salvare */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
            {isSaving && <CircularProgress size={16} />}
            {saveStatus === 'saving' && (
              <Typography variant="caption" color="text.secondary">
                Se salvează...
              </Typography>
            )}
            {saveStatus === 'saved' && (
              <Typography variant="caption" color="success.main">
                Salvat
              </Typography>
            )}
            {saveStatus === 'error' && (
              <Typography variant="caption" color="error.main">
                Eroare salvare
              </Typography>
            )}
          </Box>
          </Stack>
        </Stack>
      </Paper>

      {/* Mesaje de eroare */}
      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0.25, '& .MuiAlert-message': { fontSize: '0.78rem' } }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* Editor */}
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        {loading || loadingSegments ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {loadingSegments ? 'Se încarcă segmentele...' : 'Se încarcă raportul...'}
            </Typography>
          </Box>
        ) : (
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={content}
            onChange={handleContentChange}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            readOnly={readOnly || !selectedSegmentId}
            placeholder={!selectedSegmentId ? "Selectează un segment pentru a începe scrierea..." : "Începe să scrii raportul medical..."}
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          />
        )}
      </Paper>
      
      {/* ✨ NEW - Template Selector Dialog */}
      <TemplateSelector
        open={templateSelectorOpen}
        onClose={() => setTemplateSelectorOpen(false)}
        onApply={handleApplyTemplate}
      />
      
      {/* ✨ NEW - Save as Template Dialog */}
      <SaveAsTemplateDialog
        open={saveAsTemplateOpen}
        onClose={() => setSaveAsTemplateOpen(false)}
        contentHtml={content || ''}
        contentJson={_editorJson || { ops: [] }}
        suggestedName={currentSegment?.body_part_code || ''}
      />
      
      {/* ✨ AI Refactoring Dialog */}
      {reportId && (
        <AiRefactorComparisonDialog
          open={aiRefactorDialogOpen}
          onClose={() => setAiRefactorDialogOpen(false)}
          reportId={reportId}
          currentContent={content || ''}
          onAccept={(newContent: string) => {
            // Update editor with AI content
            setContent(newContent);
            const quill = quillRef.current?.getEditor();
            if (quill) {
              quill.root.innerHTML = newContent;
            }
            // Trigger auto-save
            setSaveStatus('idle');
          }}
        />
      )}
    </Box>
  );
};

export default ReportEditor;
